import { getCurrencyMetadata } from "../../intl/currencyRegistry.js";

export function formatCopToCurrencyRate(rate, currency) {
  const numeric = Number(rate);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "—";
  }

  if (numeric >= 1) {
    return numeric.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (numeric >= 0.01) {
    return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  return numeric.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

/** Invert API rate (1 COP = X foreign) into COP per 1 unit of foreign currency. */
export function invertCopRateToForeignUnit(rate) {
  const numeric = Number(rate);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return 1 / numeric;
}

/** Format how many COP one unit of foreign currency buys (purchasing-power framing). */
export function formatCurrencyToCopRate(rate, locale) {
  const copPerUnit = invertCopRateToForeignUnit(rate);
  if (copPerUnit == null) {
    return "—";
  }

  return copPerUnit.toLocaleString(locale || undefined, {
    maximumFractionDigits: copPerUnit >= 100 ? 0 : 2,
  });
}

/**
 * Compare first/last history points for purchasing-power direction.
 * Positive = foreign currency buys more COP now than at the start of the window.
 */
export function resolvePurchasingPowerDelta(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return null;
  }
  const start = invertCopRateToForeignUnit(points[0]?.rate);
  const end = invertCopRateToForeignUnit(points[points.length - 1]?.rate);
  if (start == null || end == null || start <= 0) {
    return null;
  }
  const absolute = end - start;
  const percent = (absolute / start) * 100;
  return {
    absolute,
    percent,
    direction: absolute > 0.5 ? "up" : absolute < -0.5 ? "down" : "flat",
  };
}

export function resolveCurrentFxRate({ displayCurrency, rates, historyLatest }) {
  const fromRates = rates?.[displayCurrency];
  if (Number.isFinite(Number(fromRates)) && Number(fromRates) > 0) {
    return Number(fromRates);
  }
  if (historyLatest?.rate != null && Number.isFinite(Number(historyLatest.rate))) {
    return Number(historyLatest.rate);
  }
  return null;
}

export function formatFxSourceLabel(source) {
  if (!source) return null;
  if (source === "openexchangerates") return "Open Exchange Rates";
  if (source === "manual") return "Manual reference";
  if (source === "fallback") return "Fallback reference";
  return String(source);
}

export function formatFxEffectiveDate(value, locale) {
  if (!value) return null;
  try {
    return new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(value).slice(0, 10);
  }
}

export function getCurrencyDisplayCode(currency) {
  return getCurrencyMetadata(currency).prefix || currency;
}
